import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, FormControl, InputLabel, Select, MenuItem, Box, FormHelperText } from '@mui/material';

const LeaveAllocationForm = ({ open, onClose, onSubmit, initialData, title = 'Add Leave Allocation', loading = false, teachers = [] }) => {
  const [formData, setFormData] = useState({
    teacher_id: '',
    total_leaves: 12,
    leaves_taken: 0,
    leave_balance: 12,
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      if (initialData && initialData.id) {
        // Editing existing allocation
        const total = Number(initialData.total_leaves || 0);
        const taken = Number(initialData.leaves_taken || 0);
        setFormData({
          teacher_id: initialData.teacher_id || '',
          total_leaves: total,
          leaves_taken: taken,
          leave_balance: Number(initialData.leave_balance ?? (total - taken)),
        });
      } else {
        // Creating new allocation - reset to defaults
        setFormData({ teacher_id: '', total_leaves: 12, leaves_taken: 0, leave_balance: 12 });
      }
      setErrors({});
    }
  }, [initialData, open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'total_leaves' || field === 'leaves_taken') {
        const total = Number(field === 'total_leaves' ? value : next.total_leaves);
        const taken = Number(field === 'leaves_taken' ? value : next.leaves_taken);
        next.leave_balance = total - taken;
      }
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.teacher_id) newErrors.teacher_id = 'Teacher is required';
    if (formData.total_leaves === '' || formData.total_leaves == null) newErrors.total_leaves = 'Total leaves is required';
    if (formData.leaves_taken === '' || formData.leaves_taken == null) newErrors.leaves_taken = 'Leaves taken is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    const payload = { ...formData };
    if (initialData?.id) payload.id = initialData.id;
    await onSubmit(payload);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box component="form" sx={{ mt: 1 }}>
          <FormControl fullWidth margin="normal" error={!!errors.teacher_id}>
            <InputLabel>Teacher</InputLabel>
            <Select value={formData.teacher_id} onChange={handleChange('teacher_id')} label="Teacher" disabled={!!initialData?.id || teachers.length === 0}>
              {teachers.length === 0 ? (
                <MenuItem disabled value="">
                  {initialData?.id ? 'No teachers available' : 'All teachers have leave allocation'}
                </MenuItem>
              ) : (
                teachers.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.full_name}</MenuItem>
                ))
              )}
            </Select>
            {errors.teacher_id && <FormHelperText>{errors.teacher_id}</FormHelperText>}
            {!initialData?.id && teachers.length === 0 && (
              <FormHelperText sx={{ color: 'warning.main' }}>
                All teachers already have leave allocation. No pending allocations.
              </FormHelperText>
            )}
          </FormControl>

          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Total Leaves"
            value={formData.total_leaves}
            onChange={handleChange('total_leaves')}
            error={!!errors.total_leaves}
            helperText={errors.total_leaves}
            inputProps={{ min: 0 }}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Leaves Taken"
            value={formData.leaves_taken}
            onChange={handleChange('leaves_taken')}
            error={!!errors.leaves_taken}
            helperText={errors.leaves_taken}
            inputProps={{ min: 0 }}
            required
          />

          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Leave Balance"
            value={formData.leave_balance}
            onChange={handleChange('leave_balance')}
            inputProps={{ min: 0 }}
            disabled
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {initialData?.id ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeaveAllocationForm;


